import api from './api';

export interface IndiceInflacion {
  anio: number;
  mes: number;
  valor: number;
}

export interface IndiceInflacionFila {
  anio: number;
  mes: number;
  valor?: number | null;
  variacion?: number | null;
}

export interface AjusteInflacionEjercicio {
  fechaDesde?: string | null;
  fechaHasta?: string | null;
  anioReferencia?: number | null;
  mesReferencia?: number | null;
  cuentaRecpam?: number | null;
  nomCuentaRecpam?: string | null;
}

export interface AjusteInflacionLinea {
  nroCuenta: number;
  nomCuenta: string;
  clase: string;
  claseDescripcion: string;
  saldoInicial: number;
  coeficienteInicial: number;
  ajusteInicial: number;
  netoMovimientos: number;
  ajusteMovimientos: number;
  ajusteTotal: number;
  saldoHistorico: number;
  saldoReexpresado: number;
}

export interface AjusteInflacionProceso {
  id: number;
  fechaDesde: string;
  fechaHasta: string;
  anioIndiceCierre: number;
  mesIndiceCierre: number;
  anioIndiceReferencia: number;
  mesIndiceReferencia: number;
  cuentaRecpam: number;
  nomCuentaRecpam: string;
  ajusteRecpam: number;
  nroAsiento?: number | null;
  estado: string;
  usuario?: string | null;
  fechaAlta: string;
  lineas: AjusteInflacionLinea[];
}

export interface AjusteInflacionResumen {
  id: number;
  fechaDesde: string;
  fechaHasta: string;
  ajusteRecpam: number;
  nroAsiento?: number | null;
  estado: string;
  usuario?: string | null;
  fechaAlta: string;
}

export interface AjusteInflacionAlerta {
  codigo: string;
  mensaje: string;
}

export interface AjusteInflacionCuentaPendiente {
  nroCuenta: number;
  nomCuenta: string;
  claseActual?: string | null;
  codigo: string;
  motivo: string;
  permiteClasificar: boolean;
}

export interface AjusteInflacionPrerrequisitos {
  alertas: AjusteInflacionAlerta[];
  cuentas: AjusteInflacionCuentaPendiente[];
}

const ajusteInflacionService = {
  getIndices: async (): Promise<IndiceInflacion[]> => {
    const response = await api.get<IndiceInflacion[]>('/api/AjusteInflacion/indices');
    return response.data ?? [];
  },

  guardarIndice: async (payload: {
    anio: number;
    mes: number;
    valor?: number | null;
    variacion?: number | null;
  }): Promise<void> => {
    await api.put('/api/AjusteInflacion/indices', payload);
  },

  eliminarIndice: async (anio: number, mes: number): Promise<void> => {
    await api.delete(`/api/AjusteInflacion/indices/${anio}/${mes}`);
  },

  importarIndices: async (filas: IndiceInflacionFila[]): Promise<{ guardados: number }> => {
    const response = await api.post<{ guardados: number }>('/api/AjusteInflacion/indices/importar', filas);
    return response.data;
  },

  getEjercicio: async (): Promise<AjusteInflacionEjercicio> => {
    const response = await api.get<AjusteInflacionEjercicio>('/api/AjusteInflacion/ejercicio');
    return response.data;
  },

  prerrequisitos: async (params: {
    fechaDesde: string;
    fechaHasta: string;
    anioReferencia?: number | null;
    mesReferencia?: number | null;
  }): Promise<AjusteInflacionPrerrequisitos> => {
    const response = await api.get<AjusteInflacionPrerrequisitos>('/api/AjusteInflacion/prerrequisitos', {
      params: {
        fechaDesde: params.fechaDesde,
        fechaHasta: params.fechaHasta,
        anioReferencia: params.anioReferencia || undefined,
        mesReferencia: params.mesReferencia || undefined,
      },
    });
    return {
      alertas: response.data?.alertas ?? [],
      cuentas: response.data?.cuentas ?? [],
    };
  },

  clasificarMasivo: async (payload: {
    nrosCuenta: number[];
    clase: string;
    mesOrigenSaldoInicial?: number | null;
    anioOrigenSaldoInicial?: number | null;
  }): Promise<{ actualizados: number }> => {
    const response = await api.post<{ actualizados: number }>('/api/AjusteInflacion/clasificar-masivo', payload);
    return response.data;
  },

  calcular: async (body: {
    fechaDesde: string;
    fechaHasta: string;
    anioReferencia?: number | null;
    mesReferencia?: number | null;
  }): Promise<AjusteInflacionProceso> => {
    const response = await api.post<AjusteInflacionProceso>('/api/AjusteInflacion/calcular', body);
    return response.data;
  },

  generar: async (id: number): Promise<AjusteInflacionProceso> => {
    const response = await api.post<AjusteInflacionProceso>(`/api/AjusteInflacion/procesos/${id}/generar`);
    return response.data;
  },

  historial: async (): Promise<AjusteInflacionResumen[]> => {
    const response = await api.get<AjusteInflacionResumen[]>('/api/AjusteInflacion/historial');
    return response.data ?? [];
  },

  getProceso: async (id: number): Promise<AjusteInflacionProceso> => {
    const response = await api.get<AjusteInflacionProceso>(`/api/AjusteInflacion/procesos/${id}`);
    return response.data;
  },
};

export default ajusteInflacionService;
